/*
   Reading the EPA fields off a printed label so the reviewer need not retype
   what is already on it.
*/

import { useMutation, type UseMutationResult } from "@tanstack/react-query"

import { http } from "@/lib/http"
import type { components } from "@/types/api.gen"

type IdentifyResponse = components["schemas"]["IdentifyResponse"]

/** What the extractor read off the artwork. Every field is optional by nature. */
export type IdentifiedLabel = components["schemas"]["ExtractedLabel"]

/**
 * Read the registration, product name and printed date off a label PDF.
 *
 * A vision call of a few seconds, so it fires once per chosen file rather than
 * per keystroke. An assist and not a gate: the review submits fine with none of
 * it, and callers fill blanks only — a value the reviewer typed always wins.
 *
 * Without an extraction key configured the route answers 503, which callers
 * treat the same as finding nothing: the fields stay typeable.
 */
export function useIdentifyLabel(): UseMutationResult<IdentifiedLabel, Error, File> {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append("file", file)
      const res = await http.post<IdentifyResponse>("/api/v1/labels/identify", form)
      return res.extracted
    },
  })
}
